import React, { memo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '../utils/cn';

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

function getHeadingId(children: React.ReactNode): string {
  const text = extractTextFromChildren(children);
  return text.toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').trim();
}

function extractTextFromChildren(children: React.ReactNode): string {
  if (typeof children === 'string') return children;
  if (typeof children === 'number') return String(children);
  if (Array.isArray(children)) return children.map(extractTextFromChildren).join('');
  if (children && typeof children === 'object' && 'props' in children) {
    return extractTextFromChildren((children as any).props.children);
  }
  return '';
}

export const MarkdownRenderer = memo(({ content, className = '' }: MarkdownRendererProps) => {
  return (
    <div className={cn("prose prose-invert max-w-none text-sm", className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Styled code blocks
          code: ({ className, children, ...props }: any) => {
            const inline = className === undefined;
            const match = /language-(\w+)/.exec(className || '');
            return !inline && match ? (
              <div className="subcard p-3 my-3 overflow-x-auto font-mono text-xs leading-relaxed text-glass-300">
                <code className={className} {...props}>
                  {children}
                </code>
              </div>
            ) : (
              <code className="bg-glass-100/10 border border-glass-200/10 px-1 py-0.5 rounded font-mono text-[0.85em] text-glass-200" {...props}>
                {children}
              </code>
            );
          },
          // Styled headings with id for anchor links
          h1: ({ children }) => {
            const id = getHeadingId(children);
            return <h1 id={id} className="text-xl font-semibold tracking-tight text-white mt-6 mb-3 scroll-mt-20">{children}</h1>;
          },
          h2: ({ children }) => {
            const id = getHeadingId(children);
            return <h2 id={id} className="text-lg font-semibold tracking-tight text-white mt-5 mb-2 scroll-mt-20">{children}</h2>;
          },
          h3: ({ children }) => {
            const id = getHeadingId(children);
            return <h3 id={id} className="text-base font-semibold text-white mt-4 mb-2 scroll-mt-20">{children}</h3>;
          },
          // Styled paragraphs
          p: ({ children }) => <p className="text-glass-300 my-2 leading-relaxed">{children}</p>,
          // Styled lists
          ul: ({ children }) => <ul className="list-disc list-inside text-glass-300 my-2 space-y-1">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal list-inside text-glass-300 my-2 space-y-1">{children}</ol>,
          li: ({ children }) => <li className="text-glass-300">{children}</li>,
          // Styled links - anchor links stay in-page, external links open new tab
          a: ({ href, children }) => {
            const isAnchor = href?.startsWith('#');
            return (
              <a
                href={href}
                className="text-accent-300 hover:text-accent-200 underline underline-offset-2"
                {...(!isAnchor && { target: "_blank", rel: "noopener noreferrer" })}
              >
                {children}
              </a>
            );
          },
          // Styled blockquotes
          blockquote: ({ children }) => (
            <blockquote className="border-l-2 border-accent-400/60 pl-4 my-3 italic text-glass-400">
              {children}
            </blockquote>
          ),
          // Styled tables
          table: ({ children }) => (
            <div className="overflow-x-auto my-4 rounded-lg border border-glass-200/10">
              <table className="table">
                {children}
              </table>
            </div>
          ),
          thead: ({ children }) => <thead>{children}</thead>,
          tbody: ({ children }) => <tbody>{children}</tbody>,
          tr: ({ children }) => <tr>{children}</tr>,
          th: ({ children }) => <th className="th">{children}</th>,
          td: ({ children }) => <td className="td text-glass-300">{children}</td>,
          // Styled horizontal rules
          hr: () => <hr className="divider my-6" />,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
});

MarkdownRenderer.displayName = 'MarkdownRenderer';
